import { memoize } from 'lodash-es';
// Query matching lifted from MentionsPlugin.tsx. PUNCTUATION is the char class that bounds
// or joins a query; LENGTH_LIMIT caps how many chars a query can match.
const LENGTH_LIMIT = 100;
const PUNCTUATION = '\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%\'"~=<>_:;';
/**
 * Trigger detection for the composer typeahead. Pure and Lexical-free: it takes the
 * text up to the caret and the registered triggers, and returns the trigger the user
 * is currently typing — or `null`.
 *
 * Explicit triggers (`@`, `/`) match a prefixed pattern with the product's exact
 * boundary and multi-word semantics (e.g. `@josh duck` is one query; `you@me` does
 * not trigger). The generic (`''`) trigger is a fallback: it activates when no
 * explicit prefix matches. So an active `@`/`/` always wins over generic
 * suggestions. By default the generic trigger stays closed on empty text; pass
 * `allowEmptyGeneric` to activate it on an empty query so a provider can show
 * default suggestions such as recent history.
 */
export function detectActiveTrigger(
/** Editor text from the line start up to the caret, e.g. `"ask about @jo"`, `"run /im"`, or `"weather to"`. */
textUpToAnchor, 
/** Registered trigger prefixes, e.g. `['@', '/', '']` (`''` is the generic fallback). */
triggers, 
/** Activate the generic (`''`) trigger on empty text. Off by default so generic providers stay closed until the user types. */
allowEmptyGeneric = false) {
    const explicitTriggers = triggers.filter((trigger) => trigger !== '');
    if (explicitTriggers.length > 0) {
        const match = matchExplicitTrigger(textUpToAnchor, getRegexes(explicitTriggers.join('')));
        if (match !== null) {
            return {
                prefix: match.replaceableString.slice(0, 1),
                query: match.matchingString,
                leadOffset: match.leadOffset,
                matchLength: match.replaceableString.length,
            };
        }
    }
    // Generic fallback: only when an explicit trigger did not match. Empty text
    // activates only opt-in providers (allowEmptyGeneric) so they can offer default
    // suggestions; otherwise the generic trigger stays closed until the user types.
    if (triggers.includes('') && (textUpToAnchor.length > 0 || allowEmptyGeneric)) {
        return { prefix: '', query: textUpToAnchor, leadOffset: 0, matchLength: textUpToAnchor.length };
    }
    return null;
}
/** Escape a character for safe literal use inside a regex character class. */
function escapeForCharClass(
/** A single trigger character, e.g. `"@"`, `"/"`, or `"-"` (which must be escaped). */
char) {
    return char.replace(/[\]\\^-]/g, '\\$&');
}
/**
 * Build the main + alias match regexes for a set of trigger characters. Building them is
 * cheap, but the registered trigger set is stable across keystrokes, so memoize by the
 * joined trigger string.
 */
const getRegexes = memoize((
/** The explicit triggers joined into one string, e.g. `"@/"`. */
triggerChars) => {
    const triggerClass = [...triggerChars].map(escapeForCharClass).join('');
    const validChars = '[^' + triggerClass + PUNCTUATION + '\\s]';
    const validJoins = '(?:' + '\\.[ |$]|' + ' |' + '[' + PUNCTUATION + ']|' + ')';
    // Each repetition matches a valid char optionally followed by a join, so spaces and
    // punctuation can appear mid-query (multi-word mentions); zero repetitions matches a bare trigger.
    const queryGroup = '((?:' + validChars + validJoins + '){0,' + LENGTH_LIMIT + '})';
    const aliasQueryGroup = '((?:' + validChars + '){0,' + LENGTH_LIMIT + '})';
    return {
        main: new RegExp('(^|\\s|\\()(' + '[' + triggerClass + ']' + queryGroup + ')$'),
        alias: new RegExp('(^|\\s|\\()(' + '[' + triggerClass + ']' + aliasQueryGroup + ')$'),
    };
});
/**
 * Match an explicit trigger at the end of the text: try the main regex, then fall back to
 * the alias (mirrors MentionsPlugin's `checkForAtSignMentions`).
 */
function matchExplicitTrigger(
/** The text up to the caret, e.g. `"ask about @jo"`. */
text, 
/** The compiled regexes for the active trigger set, from `getRegexes`. */
regexes) {
    const match = regexes.main.exec(text) ?? regexes.alias.exec(text);
    if (match === null) {
        return null;
    }
    // match[1] = leading boundary (whitespace/paren/empty), match[2] = trigger + query,
    // match[3] = query. The trigger char itself is the boundary the regex ignores.
    const leadingBoundary = match[1] ?? '';
    return {
        leadOffset: match.index + leadingBoundary.length,
        matchingString: match[3] ?? '',
        replaceableString: match[2] ?? '',
    };
}
//# sourceMappingURL=detectActiveTrigger.js.map